import { useRef, useState } from "react";
import axios from "axios";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

// One row's vendor-invoice state: attach / view / remove the vendor's invoice and set its VAT rate.
// Used in the Agent Sheet and the Trip Builder. Owner-only — never rendered for trip admins.
// onField(field, value)  -> persist a plain field (vendor_vat) — sheet uses ops PATCH, builder local state
// onRows(items, hotels)  -> refresh after an upload/delete (parent decides how to merge)
export const VendorInvoiceControl = ({ base, headers, kind, rowKey, row, onField, onRows, testid }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = `${base}/vendor-invoice/${kind}/${rowKey}`;

  if (row.self_done) {
    return (
      <span data-testid={`${testid}-selfdone-note`} className="shrink-0 text-[8px] uppercase tracking-[0.15em] text-emerald-300/70">
        Myself — no invoice needed
      </span>
    );
  }

  const hasInvoice = !!row.vendor_invoice_path;
  const hasMoney = !!(String(row.price || "").trim() || String(row.cost || "").trim());
  const missing = hasMoney && !hasInvoice && !row.no_invoice; // "no invoice" checkbox mutes the nag — there simply is none

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(url, fd, { headers });
      onRows?.(res.data.items, res.data.hotels, res.data.rentals);
      toast.success("Vendor invoice attached");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not attach the invoice.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const view = async () => {
    try {
      const res = await axios.get(url, { headers, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open the invoice.");
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.delete(url, { headers });
      onRows?.(res.data.items, res.data.hotels, res.data.rentals);
      toast.success("Invoice removed");
    } catch {
      toast.error("Could not remove it.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {missing && (
        <span data-testid={`${testid}-missing`} className="animate-pulse text-[8px] font-semibold uppercase tracking-[0.15em] text-red-400">
          No invoice
        </span>
      )}
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
      ) : hasInvoice ? (
        <>
          <button
            type="button"
            data-testid={`${testid}-view`}
            onClick={view}
            title="Vendor invoice attached — click to view"
            className="flex items-center gap-1 border border-emerald-500/50 bg-emerald-500/10 px-2 py-1 text-[9px] uppercase tracking-[0.12em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
          >
            <Paperclip className="h-3 w-3" /> Inv
          </button>
          <select
            data-testid={`${testid}-vat`}
            value={String(row.vendor_vat ?? 11)}
            onChange={(e) => onField?.("vendor_vat", e.target.value)}
            title="VAT rate on this vendor invoice"
            className="border border-neutral-700 bg-[#0d0d12] px-1 py-1 text-[9px] uppercase tracking-[0.1em] text-neutral-300 outline-none focus:border-amber-400"
          >
            <option value="11">11%</option>
            <option value="24">24%</option>
            <option value="0">0%</option>
          </select>
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={remove}
            title="Remove the attached invoice"
            className="text-neutral-600 transition-colors hover:text-red-400"
            aria-label="Remove invoice"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <label
          data-testid={`${testid}-attach`}
          title="Attach the vendor's invoice (photo or PDF)"
          className={`flex cursor-pointer items-center gap-1 border px-2 py-1 text-[9px] uppercase tracking-[0.12em] transition-colors ${
            missing
              ? "border-red-500/50 text-red-300 hover:bg-red-500 hover:text-black"
              : "border-neutral-700 text-neutral-400 hover:border-amber-400 hover:text-amber-200"
          }`}
        >
          <Paperclip className="h-3 w-3" /> Invoice
          <input ref={fileRef} data-testid={`${testid}-file`} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      )}
    </span>
  );
};
